import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  CircularProgress,
  Box,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import axios from "axios";
// Resolved from the host application, the same way axios and moment are.
import * as XLSX from "xlsx";
import { downloadStyledWorkbook } from "./versionHistoryExcel";
import { appUrl } from "../../../../../../utils/config";

/**
 * BCM-891 — Material Batch Allocation Details (version history).
 *
 * Fields run down the first column and each version is a column, ascending by
 * version number, per the spec. The first column stays put while the rest
 * scrolls horizontally, which is why it is `position: sticky` rather than a
 * plain cell.
 *
 * Reads GET /fetchAllVersionsOfAllocation. That endpoint does not exist yet, so
 * a failure is reported inside the dialog and nothing else on the page is
 * affected — the caller only renders the button that opens this when the API
 * has started returning a version number at all.
 */

/*
 * Styled to the approved mock-up: a navy header row, a tinted field column and
 * a plain white grid, with #ec6535 for the primary action.
 *
 * The family comes from the MUI theme rather than being written out here, so it
 * can never drift from the rest of the application.
 *
 * On weight: index.html loads Roboto at 300, 400 and 500 only. Anything heavier
 * is synthesised by the browser, and it synthesises differently at different
 * sizes — which is why 600, 700 and 800 mixed in one dialog looked uneven
 * against the other popups. Everything bold here is 500, a weight the font
 * actually has, and emphasis comes from size and colour instead.
 */
const BOLD = 500;
const NAVY = "#1b2541";
const GRID = "#e3e6ec";

const useStyles = makeStyles((theme) => ({
  // Grows with its content up to a fixed size; past that the table scrolls inside it.
  paper: {
    borderRadius: 12,
    minWidth: "min(1100px, calc(100vw - 64px))",
    maxWidth: "min(1440px, calc(100vw - 64px))",
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    width: "max-content",
    minWidth: "100%",
    fontFamily: theme.typography.fontFamily,
  },
  cell: {
    borderBottom: `1px solid ${GRID}`,
    borderRight: `1px solid ${GRID}`,
    padding: "11px 14px",
    fontFamily: theme.typography.fontFamily,
    fontSize: 13,
    color: "#1f2937",
    whiteSpace: "nowrap",
    background: "#fff",
    verticalAlign: "top",
  },
  // Material names run long; they wrap inside the column instead of widening it.
  wrapCell: { whiteSpace: "normal", minWidth: 150, maxWidth: 190 },
  fieldCell: {
    borderBottom: `1px solid ${GRID}`,
    borderRight: `1px solid ${GRID}`,
    borderLeft: `1px solid ${GRID}`,
    padding: "11px 14px",
    fontFamily: theme.typography.fontFamily,
    fontSize: 13,
    whiteSpace: "nowrap",
    fontWeight: BOLD,
    color: NAVY,
    background: "#f5f7fa",
    position: "sticky",
    left: 0,
    zIndex: 2,
    verticalAlign: "top",
  },
  headCell: {
    borderRight: "1px solid rgba(255,255,255,0.18)",
    padding: "14px 14px",
    fontFamily: theme.typography.fontFamily,
    fontSize: 13,
    fontWeight: BOLD,
    color: "#fff",
    background: NAVY,
    whiteSpace: "nowrap",
    textAlign: "left",
    position: "sticky",
    top: 0,
    zIndex: 3,
  },
  headFieldCell: { zIndex: 4, color: "#fff", background: NAVY, borderLeft: `1px solid ${NAVY}` },
  scroller: {
    overflowX: "auto",
    overflowY: "auto",
    // Fits all fifteen rows; scrolls beyond that or on a short screen.
    maxHeight: "min(700px, calc(100vh - 190px))",
    // Always-visible scrollbars, so extra versions are obviously there to scroll to.
    // Thick enough to grab easily with a mouse. No scrollbar-width / scrollbar-color here:
    // Chrome ignores these ::-webkit-scrollbar sizes whenever either is set.
    "&::-webkit-scrollbar": { width: 18, height: 18 },
    "&::-webkit-scrollbar-track": { background: "#e6e9ef", borderRadius: 9 },
    "&::-webkit-scrollbar-thumb": { background: "#8a94a6", borderRadius: 9, border: "2px solid #e6e9ef", minWidth: 60, minHeight: 60 },
    "&::-webkit-scrollbar-thumb:hover": { background: "#6b7588" },
    "&::-webkit-scrollbar-corner": { background: "#e6e9ef" },
  },
  title: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: BOLD,
    fontSize: 20,
    color: NAVY,
  },
  excelBtn: {
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontWeight: BOLD,
    fontSize: 13,
    marginRight: 20,
    borderRadius: 4,
    padding: "7px 18px",
    background: "#ec6535",
    color: "#fff",
    boxShadow: "none",
    "&:hover": { background: "#d9582b", boxShadow: "none" },
  },
  cancelBtn: {
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: 17,
    color: NAVY,
    padding: "4px 6px",
    minWidth: 0,
  },
  pdfBtn: {
    textTransform: "none",
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
    color: "#1f2937",
    border: "1px solid #cfd4dc",
    borderRadius: 4,
    padding: "3px 12px",
    background: "#fff",
    "&:hover": { background: "#f5f7fa" },
  },
  message: { fontFamily: theme.typography.fontFamily, fontSize: 15, padding: 32, textAlign: "center" },
}));

/*
 * Dates arrive as ISO instants and were being printed raw —
 * "2030-07-13T00:00:00.000Z" in a cell the user reads at a glance. Rendered in
 * the product's own DD-MMM-YYYY, and anything that is not a date is left
 * exactly as it came so nothing is silently reinterpreted.
 */
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T|$)/;
const formatCell = (value) => {
  const raw = String(value === null || value === undefined ? "" : value);
  if (raw === "") return "";
  // Comma-joined lists keep their shape; each part is formatted on its own.
  return raw
    .split(",")
    .map((part) => {
      const s = part.trim();
      if (!ISO_DATE.test(s)) return s;
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return s;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getUTCDate())}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`;
    })
    .join(", ");
};

const QUANTITY_ROWS = new Set(["bomQty", "netAvailable", "onHand", "plannedAllocatedQty"]);
const formatQuantity = (value) => {
  const raw = String(value === null || value === undefined ? "" : value).trim();
  if (raw === "" || !/^-?\d+(\.\d+)?$/.test(raw)) return raw;
  return Number(raw).toLocaleString("en-US", { maximumFractionDigits: 3 });
};
// On screen an empty value reads as a dash; the Excel export keeps it blank.
const displayCell = (key, value) => {
  const shown = QUANTITY_ROWS.has(key) ? formatQuantity(value) : formatCell(value);
  return shown === "" ? "\u2014" : shown;
};

// Row order is the spec's, top to bottom.
const ROWS = [
  ["material", "BMS Material Code / Description"],
  ["bomQty", "Material BoM Qty."],
  ["uom", "UOM"],
  ["storageLocation", "Storage Location ID"],
  ["batchNumber", "BMS Material Batch Number"],
  ["netAvailable", "Net Available Qty."],
  ["onHand", "On-Hand Qty."],
  ["dateOfMfg", "Date of Manufacturing"],
  ["expiryDate", "Expiry Date"],
  ["plannedAllocatedQty", "Planned Allocated Qty."],
  ["depleteInstruction", "Deplete Instruction"],
  ["plannedBatchNumber", "Planned Material Batch Number"],
  ["edaAtCMO", "Expected Date of Arrival at CMO"],
  ["countryOfOrigin", "Country of Origin"],
];

const text = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

// Shown once per material, spanning its batch columns, as in the mock-up.
const MATERIAL_ROWS = new Set(["material", "bomQty", "uom", "plannedBatchNumber", "edaAtCMO", "countryOfOrigin"]);

// The main table's wording for a lot allocated from special stock with the vendor.
const storageLocationText = (lot) => {
  const sl = lot.storageLocation;
  if (sl === undefined) return "";
  if (sl && String(sl).trim() !== "") return String(sl);
  return "Special Stocks with Vendor";
};
const joinDistinct = (values) => {
  const seen = new Set();
  values.forEach((v) => {
    const s = text(v).trim();
    if (s) seen.add(s);
  });
  return [...seen].join(", ");
};

/*
 * One column per material. A material with several allocated batches shows its
 * per-batch rows (Storage Location ID .. Deplete Instruction) comma-joined in a
 * single cell; the spec asks for those to be split into one column per batch,
 * which is left for when the endpoint exists and the real shape is known —
 * joining keeps every value visible rather than dropping any.
 */
/*
 * One column per material, and within a material one column per allocated
 * batch — the spec: "If multiple batches are allocated against a material, the
 * rows from Storage Location ID to Deplete Instruction are split into one
 * column per batch under the same version", and "If the PO has multiple
 * materials, every row of the version is split into one column per material."
 *
 * The material-level rows (material, BoM qty, UOM) repeat down each of that
 * material's batch columns, so a column is readable on its own. Planned
 * Material Batch Number, Expected Date of Arrival at CMO and Country of Origin
 * stay comma-joined across batches, exactly as the spec asks.
 */
const buildColumns = (version) => {
  const out = [];
  (version?.componentAllocationDataList || []).forEach((entry, materialIndex) => {
    const d = entry?.componentAllocationDetails || {};
    const lots = entry?.lotNumberAllocationDetails || [];
    const materialLevel = {
      material: [d.componentCode, d.componentName].filter(Boolean).join(" / "),
      bomQty: text(d.componentQuantity),
      uom: text(d.uom),
      plannedBatchNumber: text(d.plannedLotNumbers),
      edaAtCMO: text(d.expectedDateOfArrivalAtCMO),
      countryOfOrigin: text(d.countryOfOrigin),
    };
    // A material with no allocated batch still gets its column.
    const rows = lots.length > 0 ? lots : [null];
    rows.forEach((l) => {
      out.push({
        ...materialLevel,
        // Which material this column belongs to. The spec puts ONE Download
        // button against each material of a version, not one per batch:
        // "a single batch allocation PDF covering all the batches of that
        // material".
        materialIndex,
        batchLabel: l ? text(l.lotNumber) : "",
        storageLocation: l ? storageLocationText(l) : "",
        batchNumber: l ? text(l.lotNumber) : "",
        netAvailable: l ? text(l.netAvailableQuantity) : "",
        onHand: l ? text(l.onHandQuantity) : "",
        // The inventory's manufacturing date, which is what the Batch Allocation tab shows.
        dateOfMfg: l ? text(l.dateOfManufacturing) : "",
        expiryDate: l ? text(l.expiryDate) : "",
        plannedAllocatedQty: l ? text(l.plannedAllocatedQuantity) : "",
        depleteInstruction: l
          ? l.isDepletedBatch === true
            ? "Yes"
            : l.isDepletedBatch === false
            ? "No"
            : ""
          : "",
      });
    });
  });
  return out;
};

const VersionHistoryDialog = ({ open, onClose, schrockenSLID, onDownloadPdf }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [versions, setVersions] = useState([]);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!open || !schrockenSLID) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(
        `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchAllVersionsOfAllocation/${schrockenSLID}`
      )
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.versions || res?.data?.data?.versions || [];
        // Ascending by version number, as the spec requires.
        setVersions(
          [...list].sort((a, b) => Number(a.versionNumber) - Number(b.versionNumber))
        );
      })
      .catch(() => {
        if (!cancelled) setError("Unable to load the version history.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, schrockenSLID]);

  // Flatten to [{version, column}] so a version with several materials becomes
  // several columns while still showing which version it belongs to.
  const columns = [];
  const versionGroups = [];
  versions.forEach((v) => {
    const cols = buildColumns(v);
    const list = cols.length === 0 ? [{}] : cols;
    list.forEach((c) => columns.push({ version: v, column: c }));
    versionGroups.push({ versionNumber: v.versionNumber, span: list.length });
  });

  /*
   * One entry per (version, material) — the unit the PDF row's Download button
   * sits against. The spec: "one against every version where the PO has a
   * single material, and one against each material of the version where the PO
   * has multiple materials", and a material's batches share a single PDF. So a
   * material with three batches gets three columns and one button spanning them.
   */
  const materialGroups = [];
  columns.forEach((c) => {
    const last = materialGroups[materialGroups.length - 1];
    if (
      last &&
      last.version === c.version &&
      last.materialIndex === c.column.materialIndex
    ) {
      last.span += 1;
    } else {
      materialGroups.push({
        version: c.version,
        materialIndex: c.column.materialIndex,
        start: materialGroups.reduce((n, m) => n + m.span, 0),
        span: 1,
      });
    }
  });

  /*
   * "the version history data is downloaded in Excel format, in the same field
   * and version structure as displayed in the popup."
   *
   * A real .xlsx, not a CSV renamed — the spec asks for Excel format, and a CSV
   * loses the batch sub-header that the on-screen table carries. Same rows, same
   * columns, same formatting as the screen.
   */
  const downloadExcel = () => {
    const versionRow = ["Field"];
    versionGroups.forEach((g) => {
      versionRow.push(`Version ${text(g.versionNumber)}`, ...Array(g.span - 1).fill(""));
    });
    const rows = [versionRow];
    const merges = [];
    let col = 1;
    versionGroups.forEach((g) => {
      if (g.span > 1) merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + g.span - 1 } });
      col += g.span;
    });
    ROWS.forEach(([key, label]) => {
      const r = rows.length;
      const row = [label];
      if (MATERIAL_ROWS.has(key)) {
        // One value per material, merged across its batch columns, as on screen.
        materialGroups.forEach((g) => {
          const first = columns[g.start].column;
          row.push(formatCell(first[key]), ...Array(g.span - 1).fill(""));
          if (g.span > 1) merges.push({ s: { r, c: g.start + 1 }, e: { r, c: g.start + g.span } });
        });
      } else {
        // Same formatting as the table, so the export reads the way the screen
        // does rather than carrying raw ISO instants into a spreadsheet.
        columns.forEach((c) => row.push(formatCell(c.column[key])));
      }
      rows.push(row);
    });

    // Styled like the popup: version header row and field column on grey, values on white.
    downloadStyledWorkbook(
      XLSX,
      {
        rows,
        merges,
        // Width the field column to its longest label and give the version columns
        // a workable default, so the file opens readable instead of clipped.
        cols: [
          { wch: Math.max(...ROWS.map(([, l]) => l.length)) + 2 },
          ...columns.map(() => ({ wch: 28 })),
        ],
        sheetName: "Version History",
      },
      `Batch_Allocation_Version_History_${schrockenSLID}.xlsx`
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" classes={{ paper: classes.paper }}>
      <DialogTitle style={{ padding: "20px 28px 16px" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography className={classes.title}>
            Material Batch Allocation Details
          </Typography>
          <Box display="flex" alignItems="center">
            <Button
              variant="contained"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={downloadExcel}
              disabled={loading || !!error || columns.length === 0}
              className={classes.excelBtn}
            >
              Download Excel
            </Button>
            <Button
              className={classes.cancelBtn}
              endIcon={<CloseIcon style={{ fontSize: 18 }} />}
              onClick={onClose}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent style={{ padding: "0 28px 24px" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress color="primary" thickness={6} />
          </Box>
        ) : error ? (
          <Typography className={classes.message} style={{ color: "#d32f2f" }}>
            {error}
          </Typography>
        ) : columns.length === 0 ? (
          <Typography className={classes.message} style={{ color: "#5f6368" }}>
            No version history available for this PO.
          </Typography>
        ) : (
          <div className={classes.scroller}>
            <table className={classes.table}>
              <thead>
                {/*
                  One header cell per version, spanning that version's material
                  and batch columns, with the batch named underneath when a
                  version carries more than one column. Without the grouping a
                  PO with several batches shows "Version 1" three times over
                  and the reader cannot tell the batches apart.
                */}
                <tr>
                  <th className={`${classes.headCell} ${classes.fieldCell} ${classes.headFieldCell}`}>
                    Field
                  </th>
                  {versionGroups.map((g, i) => (
                    <th key={i} className={classes.headCell} colSpan={g.span}>
                      Version {text(g.versionNumber)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([key, label]) => (
                  <tr key={key}>
                    <td className={classes.fieldCell}>{label}</td>
                    {MATERIAL_ROWS.has(key)
                      ? materialGroups.map((g, i) => (
                          <td
                            key={i}
                            colSpan={g.span}
                            className={`${classes.cell} ${key === "material" ? classes.wrapCell : ""}`}
                          >
                            {displayCell(key, columns[g.start].column[key])}
                          </td>
                        ))
                      : columns.map((c, i) => (
                          <td key={i} className={classes.cell}>
                            {displayCell(key, c.column[key])}
                          </td>
                        ))}
                  </tr>
                ))}
                <tr>
                  <td className={classes.fieldCell}>Generate Batch Allocation PDF</td>
                  {/* One button per material, spanning that material's batch
                      columns — a material's batches share a single PDF. */}
                  {materialGroups.map((g, i) => (
                    <td key={i} className={classes.cell} colSpan={g.span}>
                      <Button
                        size="small"
                        className={classes.pdfBtn}
                        disabled={downloading !== null}
                        startIcon={
                          downloading === i ? (
                            <CircularProgress size={14} thickness={5} />
                          ) : (
                            <GetAppIcon style={{ fontSize: 16 }} />
                          )
                        }
                        // The whole version object is handed over, not just its
                        // number: generateBatchAllocationPDF renders the
                        // componentAllocationDataList it is given and only
                        // prints versionNumber, so the caller has to supply
                        // that version's own data.
                        onClick={async () => {
                          if (!onDownloadPdf) return;
                          setDownloading(i);
                          try {
                            await onDownloadPdf(g.version, g.materialIndex);
                          } finally {
                            setDownloading(null);
                          }
                        }}
                      >
                        {downloading === i ? "Downloading…" : "Download"}
                      </Button>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistoryDialog;
